/**
 * @flow
 */

import React, { useState } from 'react';
import gql from 'graphql-tag';
import styled from 'styled-components';
import { format } from 'date-fns';
import { useQuery, useMutation } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { get } from 'lodash';
import { FormattedMessage, defineMessages } from 'react-intl';
import {
    Form as BaseForm,
    Label,
    Input,
    Container,
    Button,
    Header,
    Menu,
} from 'semantic-ui-react';

import { Divider, PageHeader, Toggle } from '../../../components';
import DateRange from '../../../components/DateRange';
import { setShowArchive, setDate as setSalonDate } from '../../../actions/salon';

const GET_SALON_QUERY = gql`
    query getSalon($salon: ID!) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    settings {
                        enableArchive
                        reportByType
                        hideClientPhone
                        masterPanelLogin
                    }
                }
            }
        }
    }
`;

const ARCHIVE_APPOINTMENTS_MUTATION = gql`
    mutation archiveSelectedAppointments(
        $input: ArchiveSelectedAppointmentsInput!
    ) {
        archiveSelectedAppointments(input: $input) {
            appointment {
                id
                archived
                archivedAt
            }
        }
    }
`;

const UPDATE_SALON_ARCHIVE_QUERY = gql`
    mutation updateSalonArchiveSettings($input: UpdateSalonArchiveInput!) {
        updateSalonArchiveSettings(input: $input) {
            salon {
                id
                settings {
                    enableArchive
                }
            }
        }
    }
`;

const UPDATE_REPORT_TYPE_QUERY = gql`
    mutation updateSalonReportSettings($input: UpdateSalonReportInput!) {
        updateSalonReportSettings(input: $input) {
            salon {
                id
                settings {
                    reportByType
                }
            }
        }
    }
`;

const UPDATE_CLIENT_PHONE_SETTINGS = gql`
    mutation updateSalonClientPhoneSettings(
        $input: UpdateSalonClientPhoneSettingsInput!
    ) {
        updateSalonClientPhoneSettings(input: $input) {
            salon {
                id
                settings {
                    hideClientPhone
                }
            }
        }
    }
`;

const UPDATE_MASTER_LOGIN_SETTINGS = gql`
    mutation updateSalonMasterLoginSettings(
        $input: UpdateSalonMasterLoginSettingsInput!
    ) {
        updateSalonMasterLoginSettings(input: $input) {
            salon {
                id
                settings {
                    masterPanelLogin
                }
            }
        }
    }
`;

const intlMessages = defineMessages({
    salonNamePlaceholder: {
        id: 'pages.settings.generalSettings.salonName.placeholder',
        defaultMessage: 'Salon name',
    },
    salonAddressPlaceholder: {
        id: 'pages.settings.generalSettings.salonAddress.placeholder',
        defaultMessage: 'Salon address',
    },
    startAtPlaceholder: {
        id: 'pages.settings.generalSettings.startAt.placeholder',
        defaultMessage: 'Start At',
    },
    endAtPlaceholder: {
        id: 'pages.settings.generalSettings.endAt.placeholder',
        defaultMessage: 'End At',
    },
    stepPlaceholder: {
        id: 'pages.settings.generalSettings.step.placeholder',
        defaultMessage: 'Step',
    },
    formatPlaceholder: {
        id: 'pages.settings.generalSettings.format.placeholder',
        defaultMessage: 'Format',
    },
    enableArchivePlaceholder: {
        id: 'pages.settings.generalSettings.enableArchive.placeholder',
        defaultMessage: 'Enable Archive For Common Administrators',
    },
});

const Form = styled(BaseForm)`
    max-width: 200px;
`;

const OwnerSettingsPage = () => {
    const dispatch = useDispatch();
    const fromDate = useSelector(({ salon }) => salon.get('fromDate'));
    const toDate = useSelector(({ salon }) => salon.get('toDate'));
    const salonId = useSelector(({ user }) => user.get('salon'));

    const [verificationWord, setVerificationWord] = useState('');

    const { data } = useQuery(GET_SALON_QUERY, {
        variables: { salon: salonId },
        skip: !salonId,
    });
    const settings = get(data, 'viewer.salon.settings');
    const archiveToggle = settings ? settings.enableArchive : false;
    const reportToggle = settings ? settings.reportByType : false;
    const hidePhoneToggle = settings ? settings.hideClientPhone : false;
    const masterLoginToggle = settings ? settings.masterPanelLogin : false;

    const refetchQueries = [
        { query: GET_SALON_QUERY, variables: { salon: salonId } },
    ];

    const [updateReportType] = useMutation(UPDATE_REPORT_TYPE_QUERY, {
        refetchQueries,
    });
    const [updateSalonArchive] = useMutation(UPDATE_SALON_ARCHIVE_QUERY, {
        refetchQueries,
    });
    const [archiveAppointments] = useMutation(ARCHIVE_APPOINTMENTS_MUTATION);
    const [updateClientPhone] = useMutation(UPDATE_CLIENT_PHONE_SETTINGS, {
        refetchQueries,
    });
    const [updateMasterLogin] = useMutation(UPDATE_MASTER_LOGIN_SETTINGS, {
        refetchQueries,
    });

    const onReportEnable = reportByType =>
        updateReportType({
            variables: { input: { id: salonId, settings: { reportByType } } },
        });

    const onArchiveEnable = enableArchive =>
        updateSalonArchive({
            variables: { input: { id: salonId, settings: { enableArchive } } },
        }).then(() => {
            if (!enableArchive) {
                // if we switch archive off, hide archived appointments
                dispatch(setShowArchive(false));
            }
        });

    const onArchive = () =>
        archiveAppointments({
            variables: {
                input: {
                    salon: salonId,
                    fromDate: `${format(new Date(fromDate), 'yyyy-MM-dd')} 00:00`,
                    toDate: `${format(new Date(toDate), 'yyyy-MM-dd')} 23:59`,
                },
            },
        });

    const onHideClientPhone = hideClientPhone =>
        updateClientPhone({
            variables: { input: { id: salonId, settings: { hideClientPhone } } },
        });

    const onAllowMasterLogin = masterPanelLogin =>
        updateMasterLogin({
            variables: {
                input: { id: salonId, settings: { masterPanelLogin } },
            },
        });

    const setDate = (from, to) => dispatch(setSalonDate(from, to));

    return (
        <Container fluid>
            <PageHeader>
                <FormattedMessage
                    id="pages.settings.generalSettings"
                    defaultMessage="General Settings"
                />
            </PageHeader>
            <Divider />
            <Menu compact>
                <Menu.Item as="a">
                    <Toggle
                        onChange={e => {
                            onArchiveEnable(e);
                        }}
                        value={archiveToggle}
                    />
                </Menu.Item>
                <Menu.Item as="a">
                    <FormattedMessage
                        id="pages.settings.generalSettings.enableArchive"
                        defaultMessage="Enable archive for common administrators"
                    />
                </Menu.Item>
            </Menu>

            <br />
            <br />

            <Menu compact>
                <Menu.Item>
                    <Toggle
                        onChange={e => {
                            onHideClientPhone(e);
                        }}
                        value={hidePhoneToggle}
                    />
                </Menu.Item>
                <Menu.Item>
                    <FormattedMessage
                        id="pages.settings.generalSettings.hideClientPhone"
                        defaultMessage="Don't show client phone number for masters"
                    />
                </Menu.Item>
            </Menu>

            <br />
            <br />

            <Menu compact>
                <Menu.Item>
                    <Toggle
                        onChange={e => {
                            onAllowMasterLogin(e);
                        }}
                        value={masterLoginToggle}
                    />
                </Menu.Item>
                <Menu.Item>
                    <FormattedMessage
                        id="pages.settings.generalSettings.masterPanelLogin"
                        defaultMessage="Allow masters to login into administrator panel"
                    />
                </Menu.Item>
            </Menu>

            <Divider />

            <PageHeader>
                <FormattedMessage
                    id="pages.settings.generalSettings.reports"
                    defaultMessage="Reports"
                />
            </PageHeader>
            <Menu compact>
                <Menu.Item>
                    <Toggle
                        onChange={e => {
                            onReportEnable(e);
                        }}
                        value={reportToggle}
                    />
                </Menu.Item>
                <Menu.Item>
                    <FormattedMessage
                        id="pages.settings.generalSettings.enableReportByType"
                        defaultMessage="Split the amount by type"
                    />
                </Menu.Item>
            </Menu>

            <Header as="h2">
                <FormattedMessage
                    id="pages.settings.generalSettings.deleteAppointments"
                    defaultMessage="Delete appointments"
                />
            </Header>
            <DateRange
                fromDate={fromDate}
                toDate={toDate}
                onChange={setDate}
                customFormat="MMM d yyyy"
                customSize
                disabledAfter={new Date()}
            />
            <br />
            <br />
            <div>
                <FormattedMessage
                    id="pages.settings.generalSettings.delete"
                    defaultMessage='Please enter "Delete" in the field if you want to delete the history.'
                />
                <br />
                <br />
                <div>
                    <Input labelPosition="right">
                        <input
                            type="text"
                            placeholder="Delete"
                            onChange={e =>
                                setVerificationWord(e.target.value)
                            }
                        />
                        <Label color="red">
                            <Button
                                onClick={onArchive}
                                color="red"
                                disabled={verificationWord != 'Delete'}
                            >
                                <FormattedMessage
                                    id="pages.settings.generalSettings.deleteAppointments"
                                    defaultMessage="Delete appointments"
                                />
                            </Button>
                        </Label>
                    </Input>
                </div>
            </div>
        </Container>
    );
};

export default OwnerSettingsPage;
